import { type WbBenefitField } from "@shared/constants/wbBenefitFields";
import {
  BENEFIT_FIELD_TEMPLATES,
  getBenefitFieldTemplate,
} from "@shared/constants/benefitFieldTemplates";
import { buildSection, DetailTable, EmptyDetail, type DetailSection } from "./detailPrimitives";

// One raw benefit line as stored on plans.rawBenefits (see wbBenefitsParser).
export interface RawBenefitLine {
  name: string;
  value: string;
  network?: "in" | "out";
}

/**
 * Renders a plan's raw benefit lines with the SAME field set, labels,
 * grouping and order as the Plan Builder's benefits editor — so what a
 * superadmin types in the library is exactly what brokers, employers and
 * employees see. `planType` picks the per-type field template (dental,
 * vision, life, …); missing/unknown types fall back to the medical set.
 * Lines whose name isn't in the template render in a trailing
 * "Other benefits" section, so nothing is dropped.
 */
export function WbBenefitsBlock({
  raw,
  planType,
}: {
  raw: RawBenefitLine[];
  planType?: string | null;
}) {
  const template =
    getBenefitFieldTemplate(planType) ?? BENEFIT_FIELD_TEMPLATES.health;
  const lines = (raw || []).filter((line) => line?.name && String(line.value ?? "").trim());
  if (lines.length === 0) return <EmptyDetail />;

  const valueFor = (name: string, network: "in" | "out") =>
    lines.find((line) => line.name === name && (line.network ?? "in") === network)?.value ?? "";

  const display = (field: WbBenefitField): string => {
    const inValue = valueFor(field.name, "in");
    const outValue = valueFor(field.name, "out");
    if (!inValue && !outValue) return "";
    if (!outValue) return inValue;
    if (!inValue) return `Out-of-network: ${outValue}`;
    return `${inValue} · OON: ${outValue}`;
  };

  const canonicalNames = new Set(
    template.groups.flatMap((group) => group.fields.map((field) => field.name)),
  );

  const sections = template.groups.map((group) =>
    buildSection(
      group.group,
      group.fields.map((field) => ({ label: field.label, value: display(field) })),
    ),
  ).filter(Boolean) as DetailSection[];

  // Non-canonical lines (custom benefits, non-medical plans, unmapped W&B
  // lines) — grouped by name so in/out pairs collapse to one row.
  const otherNames = Array.from(
    new Set(lines.filter((line) => !canonicalNames.has(line.name)).map((line) => line.name)),
  );
  const otherSection = buildSection(
    sections.length > 0 ? "Other benefits" : undefined,
    otherNames.map((name) => ({
      label: name,
      value: display({ name, label: name }),
    })),
  );
  if (otherSection) sections.push(otherSection);

  if (sections.length === 0) return <EmptyDetail />;
  return <DetailTable sections={sections} />;
}

export function hasRawBenefits(raw: unknown): raw is RawBenefitLine[] {
  return (
    Array.isArray(raw) &&
    raw.some((line: any) => line?.name && String(line?.value ?? "").trim())
  );
}
